import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { DataService } from '../../services/data.service';
import { AuthService } from '../../services/auth.service';
import { MarketplaceResolveResponse, MarketplaceActivateRequest, MarketplaceActivateResponse } from '../../models/marketplace';

type PageState = 'loading' | 'resolved' | 'activating' | 'error' | 'no-token';

@Component({
  selector: 'app-marketplace-landing',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatSnackBarModule
  ],
  templateUrl: './marketplace-landing.component.html',
  styleUrl: './marketplace-landing.component.scss'
})
export class MarketplaceLandingComponent implements OnInit {
  state: PageState = 'loading';
  loadingMessage = 'Resolving subscription...';
  errorMessage = '';
  token = '';
  subscriptionDetails: MarketplaceResolveResponse | null = null;
  isAuthenticated = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dataService: DataService,
    private authService: AuthService,
    private snackBar: MatSnackBar
  ) {}

  ngOnInit(): void {
    // Check if user is authenticated
    this.isAuthenticated = this.authService.isAuthenticated();

    // Get token from query parameter
    this.token = this.route.snapshot.queryParams['token'];

    if (!this.token) {
      this.state = 'no-token';
      this.errorMessage = 'Invalid marketplace link. No subscription token found.';
      return;
    }

    // Resolve the subscription
    this.resolveSubscription();
  }

  /**
   * Resolve the marketplace subscription token
   */
  private resolveSubscription(): void {
    this.state = 'loading';
    this.loadingMessage = 'Resolving subscription...';

    this.dataService.resolveMarketplaceSubscription(this.token).subscribe({
      next: (response: MarketplaceResolveResponse) => {
        this.subscriptionDetails = response;
        this.state = 'resolved';

        // If user is already authenticated, proceed to activation automatically
        if (this.isAuthenticated) {
          this.activateSubscription();
        }
      },
      error: (error) => {
        this.state = 'error';
        this.errorMessage = error.message || 'Failed to resolve subscription. The token may be invalid or expired.';
        this.snackBar.open(this.errorMessage, 'Close', { duration: 5000 });
      }
    });
  }

  /**
   * Navigate to login page with return URL
   */
  goToLogin(): void {
    const returnUrl = `/marketplace/landing?token=${encodeURIComponent(this.token)}`;
    this.router.navigate(['/login'], { queryParams: { returnUrl } });
  }

  /**
   * Navigate to registration page with marketplace token
   */
  goToRegister(): void {
    this.router.navigate(['/registration'], { queryParams: { token: this.token } });
  }

  /**
   * Activate the marketplace subscription
   */
  activateSubscription(): void {
    if (!this.subscriptionDetails) {
      this.errorMessage = 'No subscription details available.';
      this.state = 'error';
      return;
    }

    this.state = 'activating';
    this.loadingMessage = 'Activating subscription...';

    const request: MarketplaceActivateRequest = {
      marketplaceSubscriptionId: this.subscriptionDetails.marketplaceSubscriptionId,
      token: this.token,
      subscriptionStatusTypeId: 1, // 1 = Active
      addSubscribeUserJustAsOwner: true
    };

    this.dataService.activateMarketplaceSubscription(request).subscribe({
      next: (response: MarketplaceActivateResponse) => {
        // Show success message
        this.snackBar.open('Subscription activated successfully!', 'Close', { duration: 3000 });

        // Refresh user plan before redirecting
        this.refreshUserPlanAndRedirect();
      },
      error: (error) => {
        this.state = 'error';
        this.errorMessage = error.message || 'Failed to activate subscription. Please try again.';
        this.snackBar.open(this.errorMessage, 'Close', { duration: 5000 });
      }
    });
  }

  /**
   * Refresh user's plan data and redirect to dashboard
   */
  private refreshUserPlanAndRedirect(): void {
    this.dataService.getUserPlan().subscribe({
      next: (plan) => {
        // Update the user's plan in auth service
        const currentUser = this.authService.getCurrentUser();
        if (currentUser) {
          currentUser.plan = plan;
          this.authService.setCurrentUser(currentUser);
        }

        // Redirect to dashboard
        this.router.navigate(['/dashboard']);
      },
      error: (error) => {
        // Even if plan refresh fails, redirect to dashboard
        console.error('Failed to refresh user plan:', error);
        this.router.navigate(['/dashboard']);
      }
    });
  }

  /**
   * Retry activation after an error
   */
  retryActivation(): void {
    if (this.subscriptionDetails) {
      this.activateSubscription();
    } else {
      this.resolveSubscription();
    }
  }
}
